import dayjs from "dayjs"
import parentApi from "store/parentApi"
import { convertQueryObjToString, localisedValue } from "utils"
import citiesTransformer from "./citiesTransformer"
import countriesTransformer from "./countriesTransformer"
import statesTransformer from "./statesTransformer"
import { convertDateTimePayload } from "utils/date"

const sharedApis = parentApi.injectEndpoints({
  endpoints: (build) => ({
    getProjectsFilter: build.query({
      query: () => `/fms/projects/projects_filter`,
      transformResponse: (response) => {
        const projects = response?.map((e) => ({
          ...e,
          key: e?.id,
          label: localisedValue({ object: e, key: "name" }),
          value: e?.id?.toString(),
        }));
        return projects;
      },
    }),

    getUnitsFilter: build.query({
      query: (params) =>
        `/fms/units/units_filter?${convertQueryObjToString(params)}`,
      transformResponse: (response) => {
        const units = response?.units?.map((e) => ({
          ...e,
          key: e?.id,
          label: e?.name,
          value: e?.id?.toString(),
        }));
        return units;
      },
      providesTags: ["fms-units-filter"],
    }),

    getPropertyCategoriesFilter: build.query({
      query: () => `/fms/property_categories/property_category_filter`,
      transformResponse: (response) => {
        const propertyCategories = response?.map((e) => ({
          ...e,
          key: e?.id,
          label: localisedValue({ object: e, key: "name" }),
          value: e?.id?.toString(),
        }));
        return propertyCategories;
      },
    }),
    getS3Presigned: build.query({
      query: ({ path }) => path,
      transformResponse: (response) => ({
        ...response,
        url: response.signedUrl || response.url,
      }),
    }),

    updateS3: build.mutation({
      query: (params) => {
        return {
          url: params.url,
          method: "PUT",
          body: params.file,
          headers: { "Content-Type": params?.type },
        };
      },
    }),

    getPropertyTypesFilter: build.query({
      query: () => `/fms/property_types/property_types_filter`,
      transformResponse: (response) => {
        const propertyTypes = response?.map((e) => ({
          ...e,
          key: e?.id,
          label: localisedValue({ object: e, key: "name" }),
          value: e?.id?.toString(),
        }));
        return propertyTypes;
      },
    }),

    getUsersFilter: build.query({
      query: () => `/v2/users/assignee_filter`,
      transformResponse: (response) => {
        const users = response?.map((e) => ({
          ...e,
          key: e?.id,
          label: localisedValue({object: e, key: "name"}),
          value: e?.id?.toString(),
        }));
        return users;
      },
    }),

    getPropertyTypes: build.query({
      query: (params) =>
        `/fms/property_types?${convertQueryObjToString(params)}`,
      transformResponse: (response) => {
        const resp = {
          ...response,
          data: response?.property_types?.map((type) => ({
            ...type,
            label: localisedValue({ object: type, key: "name" }),
            value: type?.id?.toString(),
            property_category: {
              ...type?.property_category,
              name: localisedValue({
                object: type?.property_category,
                key: "name",
              }),
            },
            property_addons: type?.property_addons?.map((addon) => ({
              ...addon,
              name: localisedValue({ object: addon, key: "name" }),
            })),
            property_attributes: type?.property_attributes?.map((att) => ({
              ...att,
              name: localisedValue({ object: att, key: "name" }),
            })),
          })),
        };
        return resp;
      },
    }),

    getCountries: build.query({
      query: () => `/countries`,
      transformResponse: (response) => countriesTransformer(response),
    }),

    getStates: build.query({
      query: (params) =>
        `/states${params ? `?${convertQueryObjToString(params)}` : ""}`,
      transformResponse: (response) => statesTransformer(response),
    }),

    getCities: build.query({
      query: (params) =>
        `/cities${params ? `?${convertQueryObjToString(params)}` : ""}`,
      transformResponse: (response) => citiesTransformer(response),
    }),

    getUnitsByProject: build.query({
      query: (params) => {
        return `/fms/units${
          params ? "?" + convertQueryObjToString(params) : ""
        }`;
      },
      transformResponse: (response) => {
        return response?.data?.map((e) => ({
          ...e,
          key: e?.id,
          label: e?.name,
          value: e?.id?.toString(),
        }));
      },
    }),

    getEditHistory: build.query({
      query: (params) => {
        return `/activities?${convertQueryObjToString(params)}`;
      },
      providesTags: ["fms-edit-history"],
    }),

    getRMSProjectsFilter: build.query({
      query: () => `/polaris/projects/projects_filter`,
      transformResponse: (response) => {
        const projects = response?.map((e) => ({
          ...e,
          key: e?.id,
          label: localisedValue({ object: e, key: "name" }),
          value: e?.id?.toString(),
        }));
        return projects;
      },
      providesTags: ["rms-projects-filter"],
    }),

    getRMSUnitsFilter: build.query({
      query: (params) =>
        `/polaris/units/units_filter?${convertQueryObjToString(params)}`,
      transformResponse: (response) => {
        const units = response?.units?.map((e) => ({
          ...e,
          key: e?.id,
          label: localisedValue({ object: e, key: "name" }),
          value: e?.id?.toString(),
        }));
        return units;
      },
      providesTags: ["rms-units-filter"],
    }),

    getRMSBookingsFilter: build.query({
      query: (params) =>
        `/polaris/v2/bookings/bookings_filter?${convertQueryObjToString(
          params,
        )}`,
      transformResponse: (response) => {
        const bookings = response?.bookings?.map((e) => ({
          ...e,
          key: e?.id,
          label: e?.booking_number,
          value: e?.id?.toString(),
        }));
        return bookings;
      },
      providesTags: ["rms-bookings-filter"],
    }),

    getRMSProjectBanksFilter: build.query({
      query: (params) =>
        `/polaris/project_banks/bank_list?${convertQueryObjToString(params)}`,
      transformResponse: (response) => {
        const projectBanks = response?.project_banks?.map((e) => ({
          ...e,
          key: e?.id,
          label: localisedValue({object: e, key: "name"}),
          value: e?.id?.toString(),
        }));
        return projectBanks;
      },
      providesTags: ["rms-project-banks-filter"],
    }),
    addRefundPayment: build.mutation({
      query: ({ id, values }) => {
        const body = {
          payment: {
            amount: -1 * parseFloat(values?.paymentAmount),
            payment_mode: values?.paymentMode,
            project_id: parseInt(values?.project),
            ...(values?.bank && { bank_id: values?.bank }),
            deposit_date: convertDateTimePayload(values?.depositDate),
            transaction_id: values?.transactionId,
            foreign_currency_amount: values?.foreignCurrencyAmount,
            ...(values?.foreignCurrency && {
              foreign_currency_code: values?.foreignCurrency,
            }),
            ...(values?.comment && {
              comments_attributes: [
                {
                  user_id: values?.userId,
                  body: values?.comment,
                  action: "create",
                },
              ],
            }),
            payment_proof_ids: values?.paymentProof?.file?.response?.id,
            tag_ids: values?.paymentTags,
          },
        };
        return {
          url: `/polaris/refunds/${id}/create_refund_payment`,
          method: "POST",
          body: body,
        };
      },
      invalidatesTags: (result, error, args) => {
        return !error
          ? ["rms-booking-payments", "booking-detail", "rms-bookings-refunds"]
          : [];
      },
    }),
    verifyPayment: build.mutation({
      query: ({ id, values }) => {
        const body = {
          payment: {
            ...(values?.comment && {
              comments_attributes: [
                {
                  user_id: values?.userId,
                  body: values?.comment,
                  action: "review",
                },
              ],
            }),
            clearance_date: convertDateTimePayload(
              dayjs(values?.clearanceDate).toISOString(),
            ),
          },
        };
        return {
          url: `/polaris/payments/${id}/verify_payment`,
          method: "PUT",
          body: body,
        };
      },
      invalidatesTags: (result, error, args) => {
        return !error ? ["rms-booking-payments", "rms-payments-listing"] : [];
      },
    }),
    rejectPayment: build.mutation({
      query: ({ id, values }) => {
        const body = {
          payment: {
            ...(values?.comment && {
              comments_attributes: [
                {
                  user_id: values?.userId,
                  body: values?.comment,
                  action: "review",
                },
              ],
            }),
            disposition_id: values?.rejectionReason,
          },
        };
        return {
          url: `/polaris/payments/${id}/reject_payment`,
          method: "PUT",
          body: body,
        };
      },
      invalidatesTags: (result, error, args) => {
        return !error ? ["rms-booking-payments", "rms-payments-listing"] : [];
      },
    }),

    getUnitsForImportBooking: build.query({
      query: (params) =>
        `/polaris/v2/bookings/fetch_available_units?${convertQueryObjToString(
          params,
        )}`,
      transformResponse: (response) => {
        const units = response?.available_units?.map((e) => ({
          ...e,
          key: e?.unit_id,
          label: e?.unitNumber,
          value: e?.unit_id?.toString(),
        }));
        return units;
      },
      providesTags: ["rms-import-booking-units"],
    }),

    getUsersFilterV2: build.query({
      query: (params) =>
        `/v2/users/assignee_filter?${convertQueryObjToString(params)}`,
      transformResponse: (response) => {
        const users = response?.map((e) => ({
          ...e,
          key: e?.id,
          label: localisedValue({object: e, key: "name"}),
          value: e?.id?.toString(),
        }));
        return users;
      },
    }),

    getPropertyTypesRMS: build.query({
      query: (params) =>
        `/polaris/property_types?${convertQueryObjToString(params)}`,
      transformResponse: (response) => {
        const resp = {
          ...response,
          property_types: response?.property_types?.map((category) => ({
            ...category,
            label: localisedValue({ object: category, key: "name" }),
            value: category?.id?.toString(),
            property_attributes: category?.property_attributes?.map((item) => ({
              ...item,
              label: localisedValue({ object: item, key: "name" }),
              value: item?.id?.toString(),
            })),
            property_addons: category?.property_addons?.map((item) => ({
              ...item,
              label: localisedValue({ object: item, key: "name" }),
              value: item?.id?.toString(),
            })),
          })),
        };
        return resp;
      },
    }),

    getNotifications: build.query({
      query: (params) => `/notifications?${convertQueryObjToString(params)}`,
      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        return {};
      },
      forceRefetch({ currentArg, previousArg }) {
        return currentArg?.page !== previousArg?.page;
      },
      merge: (currentData, incomingData, option) => {
        if (!currentData) {
          return incomingData;
        }

        return {
          ...currentData,
          ...incomingData,
          today_notifications: [
            ...(currentData.today_notifications || []),
            ...(incomingData.today_notifications || []),
          ],
          yesterday_notifications: [
            ...(currentData.yesterday_notifications || []),
            ...(incomingData.yesterday_notifications || []),
          ],
          older_notifications: [
            ...(currentData.older_notifications || []),
            ...(incomingData.older_notifications || []),
          ],
          pagination: incomingData?.pagination,
          unread_count: incomingData?.unread_count,
        };
      },
    }),

    markAsReadNotification: build.mutation({
      query: (id) => ({
        url: `/notifications/${id}/mark_as_read`,
        method: "PUT",
      }),
    }),

    markAllAsReadNotification: build.mutation({
      query: () => ({
        url: `/notifications/mark_all_as_read`,
        method: "PUT",
      }),
    }),

    getRMSClientsFilter: build.query({
      query: () => `/polaris/units/clients_filter`,
      transformResponse: (response) => {
        const clients = response?.clients?.map((e) => ({
          ...e,
          key: e?.id,
          label: localisedValue({ object: e, key: "name" }),
          value: e?.id?.toString(),
        }));
        return clients;
      },
    }),
    addTransferPayment: build.mutation({
      query: (values) => {
        const body = {
          payment: {
            amount: values?.paymentAmount,
            payment_mode: values?.paymentMode,
            project_id: values?.project,
            ...(values?.bank && { bank_id: values?.bank }),
            deposit_bank_id: values?.depositedIn,
            deposit_date: convertDateTimePayload(values?.depositDate),
            transaction_id: values?.transactionId,
            transfer_id: values?.transferId,
            foreign_currency_amount: values?.foreignCurrencyAmount,
            ...(values?.foreignCurrency && {
              foreign_currency_code: values?.foreignCurrency,
            }),
            ...(values?.comment && {
              comments_attributes: [
                {
                  user_id: values?.userId,
                  body: values?.comment,
                  action: "create",
                },
              ],
            }),
            payment_proof_ids: [values?.paymentProof?.file?.response?.id],
            tag_ids: values?.paymentTags,
          },
        };
        return {
          url: `/polaris/transfers/${values?.id}/collect_transfer_fee`,
          method: "POST",
          body: body,
        };
      },
      invalidatesTags: (result, error, args) => {
        return !error ? ["rms-transfers-listing"] : [];
      },
    }),

    getTimezones: build.query({
      query: () => `/tenants/timezones_filter`,
      transformResponse: (response) => {
        return response?.timezones?.map((item) => ({
          key: item?.id,
          label: item?.id,
          value: item?.id,
        }));
      },
    }),

    getCurrencies: build.query({
      query: () => `/tenants/currencies_filter`,
      transformResponse: (response) => {
        return response?.currencies?.map((item) => ({
          key: item?.id,
          label: item?.symbol,
          value: item?.code,
        }));
      },
    }),

    getTenantLanguages: build.query({
      query: () => `/tenants/languages_filter`,
      transformResponse: (response) => {
        return response?.languages?.map((item) => ({
          key: item?.id,
          label: localisedValue({ object: item, key: "name" }),
          value: item?.code,
        }));
      },
    }),

    getActivityLogs: build.query({
      query: (params) => {
        return `/message_logs?${convertQueryObjToString(params)}`;
      },
      providesTags: ["activity-logs"],
    }),

    getAreaMetrics: build.query({
      query: () => `/tenants/area_units_filter`,
      transformResponse: (response) => {
        return response?.area_units?.map((item) => ({
          key: item?.slug,
          label: localisedValue({ object: item, key: "name" }),
          value: item?.slug,
        }));
      },
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetProjectsFilterQuery,
  useLazyGetProjectsFilterQuery,
  useGetUnitsFilterQuery,
  useLazyGetUnitsFilterQuery,
  useUpdateS3Mutation,
  useLazyGetS3PresignedQuery,
  useLazyGetEditStaffsQuery,
  useGetPropertyTypesFilterQuery,
  useGetPropertyCategoriesFilterQuery,
  useGetUsersFilterQuery,
  useLazyGetUsersFilterQuery,
  useGetPropertyTypesQuery,
  useLazyGetPropertyTypesQuery,
  useGetCountriesQuery,
  useLazyGetCountriesQuery,
  useGetStatesQuery,
  useLazyGetStatesQuery,
  useGetCitiesQuery,
  useLazyGetCitiesQuery,
  useGetUnitByProjectQuery,
  useLazyGetUnitsByProjectQuery,
  useLazyGetEditHistoryQuery,
  useGetRMSProjectsFilterQuery,
  useLazyGetRMSProjectsFilterQuery,
  useGetRMSUnitsFilterQuery,
  useLazyGetRMSUnitsFilterQuery,
  useLazyGetRMSBookingsFilterQuery,
  useGetRMSBookingsFilterQuery,
  useLazyGetRMSProjectBanksFilterQuery,
  useGetRMSProjectBanksFilterQuery,
  useAddRefundPaymentMutation,
  useVerifyPaymentMutation,
  useRejectPaymentMutation,
  useGetUnitsForImportBookingQuery,
  useLazyGetUnitsForImportBookingQuery,
  useGetUsersFilterV2Query,
  useLazyGetUsersFilterV2Query,
  useGetPropertyTypesRMSQuery,
  useLazyGetPropertyTypesRMSQuery,
  useGetNotificationsQuery,
  useLazyGetNotificationsQuery,
  useMarkAsReadNotificationMutation,
  useMarkAllAsReadNotificationMutation,
  useGetCurrenciesQuery,
  useLazyGetCurrenciesQuery,
  useGetTimezonesQuery,
  useLazyGetTimezonesQuery,
  useGetTenantLanguagesQuery,
  useLazyGetTenantLanguagesQuery,
  useGetActivityLogsQuery,
  useLazyGetActivityLogsQuery,
  useGetRMSClientsFilterQuery,
  useLazyGetRMSClientsFilterQuery,
  useAddTransferPaymentMutation,
  useGetAreaMetricsQuery,
  useLazyGetAreaMetricsQuery,
} = sharedApis
